<script lang="ts" setup>
import { useToast } from '@solfacil/girassol';

import { useDownloadOrderDocumentLazyQuery, type FindOrderOut } from '~/types/ecommerce/generated.codegen';

const props = defineProps<{
  order: FindOrderOut;
}>();

const triangulation = computed(() => props.order.triangulation);
const documents = computed(() => props.order.documents);

const { createErrorToast, createInformativeToast } = useToast();

const {
  load: fetchFileDownload,
  onResult: onDownloadFileResult,
  onError: onDownloadFileError
} = useDownloadOrderDocumentLazyQuery({}, {
  fetchPolicy: 'no-cache'
});

onDownloadFileResult((result) => {
  const url = result.data.downloadOrderDocument?.url;
  if (url) {
    navigateTo(url, {
      external: true,
      open: { target: '_blank' }
    });
  } else {
    createErrorToast('Erro ao baixar o documento');
  }
});

onDownloadFileError(() => {
  createErrorToast('Erro ao baixar o documento');
});

function getDowloadDocumento (documentName: string | null) {
  fetchFileDownload(null, {
    order_id: props.order.id,
    file_name: documentName
  });
  createInformativeToast('Gerando link para o documento');
}
</script>

<template>
  <div class="order-documents-content">
    <div class="header">
      <span class="title">Documentos
        <SolTag
          v-if="documents.length"
          id="informative"
          variant="fill"
        >
          {{ triangulation?.invoice_number ? 'Venda direta (triangulação)' : 'Outras formas de pagamento' }}
        </SolTag>
      </span>
      <div v-if="documents.length" class="links">
        <SolLink
          v-for="(document, index) in documents"
          :id="`document-${index}`"
          :key="index"
          size="medium"
          @click="getDowloadDocumento(document)"
        >
          {{ document }}
        </SolLink>
        <div v-if="triangulation?.invoice_number" class="triangulation">
          <span class="title-triangulation">Série da Nota Fiscal</span>
          <span class="description">
            {{ triangulation?.invoice_number }}
          </span>
        </div>
        <div v-if="triangulation?.invoice_serial_number" class="triangulation">
          <span class="title-triangulation">Número da Nota Fiscal</span>
          <span class="description">
            {{ triangulation?.invoice_serial_number }}
          </span>
        </div>
        <div v-if="triangulation?.invoice_issue_date" class="triangulation">
          <span class="title-triangulation">Data da Emissão da Nota Fiscal</span>
          <span class="description">
            {{ triangulation?.invoice_issue_date }}
          </span>
        </div>
      </div>
      <div v-else class="info-content">
        <SolEmptyState
          id="empty-state"
          title="Pedido sem documentos anexados"
          subtitle="Você não adicionou nenhum documento neste pedido."
          variant="no-content"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.order-documents-content {
  @apply flex flex-col gap-4xs px-4xs py-2xs;
  @apply border-1 border-neutral-high-medium rounded-lg;

  .header {
    @apply flex flex-col gap-2xs;

    > .title {
      @apply fonts-heading-h3;
      @apply flex flex-row xsm:site:flex-col align-middle;
      @apply gap-2 md:site:gap-4 lg:site:gap-4;
    }
    > .links {
      @apply flex flex-col gap-2 justify-start;
      > .triangulation {
        @apply flex flex-col mt-4 xsm:site:mt-3;
        > .title-triangulation {
          @apply fonts-body-small-regular
          @apply text-neutral-low-light;
        }
        > .description {
          @apply fonts-body-medium-regular;
        }
      }
    }
  }

  .info-content {
    @apply flex flex-col gap-4xs;
  }
}
</style>
